import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MessageCircle, Link2, Unlink, ExternalLink, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function TelegramConnect({ token, userType = "admin" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLinked, setIsLinked] = useState(false);
  const [linkedAt, setLinkedAt] = useState(null);
  const [telegramLink, setTelegramLink] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);

  useEffect(() => {
    checkTelegramStatus();
  }, []);

  const checkTelegramStatus = async () => {
    try {
      const res = await fetch(`${API}/telegram/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setIsLinked(data.linked);
        setLinkedAt(data.linked_at);
      }
    } catch (err) {
      console.error("Failed to check Telegram status:", err);
    } finally {
      setCheckingStatus(false);
    }
  };

  const generateLink = async () => {
    setLoading(true);
    try {
      let endpoint;
      if (userType === "contractor") {
        endpoint = `${API}/telegram/contractor/generate-link`;
      } else if (userType === "supplier") {
        endpoint = `${API}/telegram/supplier/generate-link`;
      } else if (userType === "hospital") {
        endpoint = `${API}/telegram/hospital/generate-link`;
      } else {
        endpoint = `${API}/telegram/generate-link`;
      }
      
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ user_type: userType })
      });
      
      if (res.ok) {
        const data = await res.json();
        setTelegramLink(data.telegram_link);
      } else {
        toast.error("Failed to generate link");
      }
    } catch (err) {
      toast.error("Failed to generate Telegram link");
    } finally {
      setLoading(false);
    }
  };

  const disconnectTelegram = async () => {
    if (!confirm("Disconnect Telegram? You'll stop receiving notifications.")) return;
    
    try {
      const res = await fetch(`${API}/telegram/disconnect`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        setIsLinked(false);
        setLinkedAt(null);
        toast.success("Telegram disconnected");
      }
    } catch (err) {
      toast.error("Failed to disconnect");
    }
  };

  const openTelegramLink = () => {
    if (telegramLink) {
      window.open(telegramLink, "_blank");
      // Start polling to check if linked
      const pollInterval = setInterval(async () => {
        const res = await fetch(`${API}/telegram/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.linked) {
            setIsLinked(true);
            setLinkedAt(data.linked_at);
            setIsOpen(false);
            setTelegramLink("");
            clearInterval(pollInterval);
            toast.success("Telegram connected successfully!");
          }
        }
      }, 3000);
      
      // Stop polling after 2 minutes
      setTimeout(() => clearInterval(pollInterval), 120000);
    }
  };

  if (checkingStatus) {
    return (
      <Button variant="ghost" size="sm" disabled>
        <Loader2 className="w-4 h-4 animate-spin" />
      </Button>
    );
  }

  return (
    <>
      {isLinked ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsOpen(true)}
          className="text-green-600 hover:text-green-700 hover:bg-green-50"
          title="Telegram Connected"
          data-testid="telegram-connected-btn"
        >
          <CheckCircle2 className="w-4 h-4 mr-1" />
          <MessageCircle className="w-4 h-4" />
        </Button>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => { setIsOpen(true); generateLink(); }}
          className="text-slate-600 hover:text-blue-600 hover:bg-blue-50"
          title="Connect Telegram"
          data-testid="telegram-connect-btn"
        >
          <MessageCircle className="w-4 h-4" />
        </Button>
      )}

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-blue-500" />
              {isLinked ? "Telegram Connected" : "Connect Telegram"}
            </DialogTitle>
            <DialogDescription>
              {isLinked 
                ? "You're receiving real-time notifications via Telegram."
                : "Get instant notifications on your phone via Telegram."
              }
            </DialogDescription>
          </DialogHeader>

          {isLinked ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-green-700">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="font-medium">Connected to AskIsaac Bot</span>
                </div>
                {linkedAt && (
                  <p className="text-sm text-green-600 mt-1">
                    Linked on {new Date(linkedAt).toLocaleDateString()}
                  </p>
                )}
              </div>
              
              <div className="text-sm text-slate-600">
                <p className="font-medium mb-2">You'll receive notifications for:</p>
                <ul className="space-y-1 text-slate-500">
                  <li>• New maintenance requests</li>
                  <li>• New backorder requests</li>
                  <li>• Urgent alerts</li>
                  <li>• Job completions</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                </div>
              ) : telegramLink ? (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-700 mb-3">
                      Click the button below to open Telegram and connect your account:
                    </p>
                    <Button 
                      onClick={openTelegramLink}
                      className="w-full bg-[#0088cc] hover:bg-[#0077b5]"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Open Telegram
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 text-center">
                    This link expires in 10 minutes. After clicking, press "Start" in Telegram.
                  </p>
                </>
              ) : (
                <div className="text-center py-4">
                  <Button onClick={generateLink}>
                    <Link2 className="w-4 h-4 mr-2" />
                    Generate Connection Link
                  </Button>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex gap-2 sm:gap-0">
            {isLinked && (
              <Button 
                variant="outline" 
                onClick={disconnectTelegram}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Unlink className="w-4 h-4 mr-2" />
                Disconnect
              </Button>
            )}
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
